/**
 * แคว้นเก้ากระบี่ (jiuzhou): the content hooks of the empty country, as data (docs/handoff/newdir/REGION.md).
 * All coordinates are REGION metres (x east, y south), not the town's.
 *
 * - `JIUZHOU_BOSS_SITES`: where the three world bosses live in the region (docs/systems/WORLD_BOSSES.md §1.1, "region home").
 *   The world-boss rules reference a site by `boss` id; this file never touches them.
 * - `JIUZHOU_CAMPS`: mob camps in the grass and forests (systems/activity.ts sets one down when a player's active
 *   cells first touch it), by distance from the town: gentle near it, harder further out.
 * - `JIUZHOU_SECRETS`: vistas and chests of the peaks, ruins and meadows (systems/secrets.ts; only on a region map).
 * - `JIUZHOU_SUPPLY_ROUTES`: supply-run roads for the guild's supply run (docs/systems/GUILD_V2.md §1.3): the road line, the
 *   ambush points and the mode each offers (npc | pvp).
 */
import type { RegionWild, XY } from '../../region/sketch';
import type { SecretDef } from '../secrets';
import type { WorldBossId } from '../worldBosses';

export interface BossSite {
  readonly id: string;
  /** The world boss that lives here (`data/worldBosses.ts`). */
  readonly boss: WorldBossId;
  readonly name: string;
  /** The hunting ground (`wilds` id) it lies in, if any. */
  readonly wild?: string;
  /** Leash / attendance circle (the boss row's `arena` in region metres). */
  readonly arena: { readonly x: number; readonly y: number; readonly r: number };
  /** The fight box: [x0, x1, y0, y1], the arena's square. */
  readonly box: readonly [number, number, number, number];
  /** Where the boss first stands. */
  readonly spawn: { readonly x: number; readonly y: number };
  /** The way in from the nearest road, on foot, to the arena's edge. */
  readonly approach: { readonly name: string; readonly from: string; readonly pts: readonly XY[] };
  /** Where the fight-watchers and the late arrivals stand (spectator anchors on open ground, outside the leash). */
  readonly watch: readonly XY[];
}

const box = (x: number, y: number, r: number): readonly [number, number, number, number] => [x - r, x + r, y - r, y + r];

export const JIUZHOU_BOSS_SITES: readonly BossSite[] = [
  {
    id: 'jz-site-ape',
    boss: 'wb-ape',
    name: 'ซากวัดร้างทะเลไผ่',
    wild: 'bamboo-sea',
    arena: { x: 2160, y: 2520, r: 90 },
    box: box(2160, 2520, 90),
    spawn: { x: 2150.5, y: 2520.5 },
    approach: {
      name: 'ทางทะเลไผ่ (ทางแยกเข้าวัดร้าง)',
      from: 'ทางทะเลไผ่',
      pts: [[1900, 2496], [1989.5, 2503.5], [2059.5, 2513.5], [2100, 2518]],
    },
    watch: [[2015.5, 2564.5], [2072.5, 2472.5]],
  },
  {
    id: 'jz-site-firebird',
    boss: 'wb-firebird',
    name: 'ผาแดงตะวันออก',
    arena: { x: 2700, y: 836, r: 130 },
    box: box(2700, 836, 130),
    spawn: { x: 2731.5, y: 843.5 },
    approach: {
      name: 'ทางขึ้นผาแดง (แยกจากถนนผาแดง; เชิงผาเดินถึง ที่เหลือบินหรือปีน)',
      from: 'ถนนผาแดง',
      pts: [[2490, 900], [2520, 885], [2540, 880]],
    },
    watch: [[2520, 820], [2530, 940]],
  },
  {
    id: 'jz-site-turtle',
    boss: 'wb-thunder-turtle',
    name: 'ปากอ่าวระหว่างประภาคารกับอ่าวโจรสลัด',
    arena: { x: 1885, y: 3311, r: 90 },
    box: box(1885, 3311, 90),
    spawn: { x: 1850.5, y: 3311.5 },
    approach: {
      name: 'ทางเลียบผาตะวันออก (ลงชายหาดอ่าวโจร)',
      from: 'ทางเลียบผาตะวันออก',
      pts: [[2120, 3156], [2050, 3220], [1990, 3280], [1935, 3302]],
    },
    watch: [[1960, 3270], [1950, 3340]],
  },
];

export const bossSiteOf = (boss: WorldBossId): BossSite | undefined => JIUZHOU_BOSS_SITES.find((s) => s.boss === boss);

const camp = (id: string, place: number, name: string, rect: readonly [number, number, number, number], spawns: RegionWild['spawns']): RegionWild => ({
  id,
  place,
  name,
  rect,
  spawns,
});

/** Camps in the empty grass and forests, easy near the town and harder with distance (place numbers 100+ are new). */
export const JIUZHOU_CAMPS: readonly RegionWild[] = [
  camp('jz-boar-meadow', 101, 'ทุ่งหมูป่าชายฝั่ง', [1800, 1980, 2700, 2860], [{ monster: 'boar', n: 10 }, { monster: 'viper', n: 5 }]),
  camp('jz-reed-bank', 102, 'ริมหนองกกตะวันออก', [2150, 2400, 2900, 3080], [{ monster: 'viper', n: 8 }, { monster: 'wolf', n: 6 }]),
  camp('jz-north-toll', 103, 'ด่านเก็บค่าผ่านทางเหนือ', [330, 560, 2700, 2880], [{ monster: 'bandit', n: 8 }, { monster: 'knifer', n: 3 }]),
  camp('jz-pine-ridge', 104, 'สันเขาสนตะวันตก', [520, 860, 1500, 1700], [{ monster: 'leopard', n: 6 }, { monster: 'boar', n: 6 }]),
  camp('jz-east-grass', 105, 'ทุ่งหญ้าตะวันออก', [2420, 2640, 2000, 2300], [{ monster: 'scorpion', n: 8 }, { monster: 'python', n: 4 }]),
  camp('jz-far-west', 106, 'ป่าลึกตะวันตกไกล', [200, 500, 1300, 1500], [{ monster: 'foxspirit', n: 5 }, { monster: 'spider', n: 6 }]),
  camp('jz-gorge-mouth', 107, 'ปากหุบเขาใต้', [820, 1020, 2800, 3000], [{ monster: 'bloodbat', n: 6 }, { monster: 'centipede', n: 5 }]),
  camp('jz-plateau', 108, 'ที่ราบสูงเหนือ', [2000, 2300, 600, 800], [{ monster: 'bear', n: 4 }, { monster: 'leopard', n: 5 }]),
  camp('jz-rice-edge', 109, 'ชายนาข้าวตะวันออก', [2500, 2700, 1500, 1700], [{ monster: 'wolf', n: 8 }, { monster: 'bandit', n: 5 }]),
];

const rv = (id: string, name: string, x: number, y: number, h: number): SecretDef => ({ id, kind: 'vista', name, zone: 'yanyu2', x, y, h });
const rc = (id: string, tier: 1 | 2 | 3, name: string, x: number, y: number, h: number): SecretDef => ({ id, kind: 'chest', tier, name, zone: 'yanyu2', x, y, h });

/** Vistas and chests of the region (region metres; heights above the sea). */
export const JIUZHOU_SECRETS: readonly SecretDef[] = [
  rv('rv.golden', 'ยอดเขาสีทอง', 528.5, 2456.5, 380),
  rv('rv.redtop', 'ยอดผาแดง', 2731.5, 843.5, 178),
  rv('rv.lighthouse', 'ยอดประภาคารเก่า', 1760.5, 3336.5, 36.47),
  rv('rv.blackpagoda', 'เจดีย์หักวัดกระบี่ทมิฬ', 1760.5, 901.5, 37.25),
  rv('rv.cloud', 'ต้นลำธารเมฆา', 633.5, 2641.5, 64.23),
  rv('rv.pirate', 'หอดูเรือโจรสลัด', 2040.5, 3306.5, 45.95),
  rc('rc.bamboohall', 2, 'หลังคาวิหารวัดร้างในทะเลไผ่', 2230.5, 2481.5, 63.88),
  rc('rc.bandit', 2, 'หิ้งหินค่ายโจรภูเขา', 700.5, 1296.5, 33.53),
  rc('rc.graves', 3, 'หลุมลับสุสานใต้เขา', 760.5, 1450.5, 25.96),
  rc('rc.reed', 1, 'ใต้กอกกริมหนองตะวันออก', 2300.5, 3000.5, 21.17),
  rc('rc.toll', 1, 'หลังด่านเก็บค่าผ่านทางเหนือ', 440.5, 2800.5, 68.67),
  rc('rc.pine', 2, 'ใต้สนเอนสันเขาตะวันตก', 700.5, 1600.5, 24.1),
  rc('rc.plateau', 2, 'กลางที่ราบสูงเหนือ', 2150.5, 700.5, 25.76),
  rc('rc.farwest', 3, 'ถ้ำป่าลึกตะวันตกไกล', 343.5, 1405.5, 113.51),
  rc('rc.rice', 1, 'กระท่อมชายนาข้าว', 2600.5, 1600.5, 31.35),
  rc('rc.eastgrass', 1, 'โขดหินกลางทุ่งหญ้า', 2530.5, 2150.5, 34.29),
  rc('rc.boar', 1, 'ใต้ต้นไม้ทุ่งหมูป่า', 1800.5, 2780.5, 0.05),
];

export interface SupplyAmbush {
  readonly id: string;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  /** Monsters (data/monsters.ts) that jump the load here in `npc` mode. */
  readonly monsters: readonly { readonly monster: string; readonly n: number }[];
}

export interface SupplyRoute {
  readonly id: string;
  readonly name: string;
  /** Station ids of the region's sketch (data/regions/jiuzhou.ts `stations`) at its ends. */
  readonly from: string;
  readonly to: string;
  /** The road the load keeps to, region metres. */
  readonly pts: readonly XY[];
  readonly ambushes: readonly SupplyAmbush[];
  /** The standing rule: every activity offers npc or pvp (docs/handoff/newdir/QUEUE.md). */
  readonly modes: readonly ('npc' | 'pvp')[];
}

const BOTH = ['npc', 'pvp'] as const;

export const JIUZHOU_SUPPLY_ROUTES: readonly SupplyRoute[] = [
  {
    id: 'jz-sr-trunk',
    name: 'ถนนหลวง หยานหยู–ลานศิลา',
    from: 'yanyu2',
    to: 'jz-hub',
    pts: [[1488, 2500], [1500, 2406], [1476, 2276], [1476, 2222], [1450, 2194], [1450, 1846]],
    ambushes: [
      { id: 'jz-sr-trunk.way', name: 'ศาลาพักม้าสิบลี้', x: 1510, y: 2365, r: 30, monsters: [{ monster: 'bandit', n: 3 }, { monster: 'wolf', n: 2 }] },
      { id: 'jz-sr-trunk.south', name: 'ชานเมืองใต้', x: 1450, y: 2060, r: 30, monsters: [{ monster: 'knifer', n: 2 }, { monster: 'bandit', n: 3 }] },
    ],
    modes: BOTH,
  },
  {
    id: 'jz-sr-bamboo',
    name: 'ทางทะเลไผ่ ไปทะเลสาบสราญ',
    from: 'yanyu2',
    to: 'lake',
    pts: [[1500, 2406], [1700, 2456], [1900, 2496], [2100, 2356], [2250, 2206], [2250, 2016], [2140, 1876]],
    ambushes: [
      { id: 'jz-sr-bamboo.ruin', name: 'ซากวัดร้างทะเลไผ่', x: 2000, y: 2480, r: 40, monsters: [{ monster: 'spider', n: 3 }, { monster: 'wolf', n: 3 }] },
      { id: 'jz-sr-bamboo.fields', name: 'ทุ่งหมาป่าเงาจันทร์', x: 2230, y: 2230, r: 40, monsters: [{ monster: 'wolf', n: 5 }] },
    ],
    modes: BOTH,
  },
  {
    id: 'jz-sr-mist-peak',
    name: 'ถนนช่องเขาตะวันตก ไปเชิงเขายอดหมอก',
    from: 'yanyu2',
    to: 'wudangfoot',
    pts: [[1188, 2886], [1150, 2856], [1060, 2766], [960, 2666]],
    ambushes: [
      { id: 'jz-sr-mist-peak.gorge', name: 'ปากหุบเขาใต้', x: 1060, y: 2766, r: 35, monsters: [{ monster: 'bloodbat', n: 3 }, { monster: 'centipede', n: 2 }] },
    ],
    modes: BOTH,
  },
  {
    id: 'jz-sr-lake',
    name: 'ถนนตะวันออก ลานศิลา–ทะเลสาบสราญ',
    from: 'jz-hub',
    to: 'lake',
    pts: [[1478, 1778], [1490, 1806], [1700, 1806], [1900, 1806], [2000, 1826], [2100, 1866], [2160, 1896]],
    ambushes: [
      { id: 'jz-sr-lake.mid', name: 'กลางทางถนนตะวันออก', x: 1900, y: 1806, r: 40, monsters: [{ monster: 'bandit', n: 4 }, { monster: 'knifer', n: 2 }] },
    ],
    modes: BOTH,
  },
];
